/**
 * Change Game overlay: the picker a solo player, or a room's P1, uses to
 * switch games without leaving the room.
 *
 * All games uses the shared search and recents store. Browse sources use
 * local filtering. The rail is a vertical tablist on
 * wide screens and a scrolling tab row on phones (CSS only; the markup is the
 * same). The search box and the achievements / high-score toggles apply to
 * whichever list is showing, and their state carries across sources so one
 * query can be tried system by system.
 *
 * Rows render immediately. A system's achievement and score definitions are
 * tens of MB, so the row icons are stamped in afterwards (stampBadges) once
 * `cheevosEngine.loadSystem` resolves; a list whose systems define neither
 * collapses its icon column instead of leaving an empty gutter.
 *
 * NESPlay keeps one-line delegates (_showGameChangeOverlay and friends) with
 * the old names, so tests and cross-module callers drive `app._x()`. Picking
 * a row calls back into `app._requestGameChange`, which owns the solo and
 * netplay switch flows. Never imports main.js.
 */
import { badgeIconEl } from '../../shared/components/badge-icons.js';
import { systemTag } from '../../shared/system-label.js';
import { mergedSystems } from '../../shared/system-merge.js';
import { scoreSpecSupported } from '../../shared/score-decode.js';
import { authState } from '../auth.js';
import { moduleRegistry } from '../module-registry.js';
import { buildCoverLabel } from '../home/cover-label.js';
import { loadScopes, saveScopes } from './global-search.js';
import { createGameChangeSearch } from './game-change-search.js';
import { noteSurfaceOpened } from './surface-order.js';

/** Homepage order and labels. */
const SYSTEM_ORDER = [
  { id: 'atari2600', label: 'Atari 2600' },
  { id: 'c64', label: 'C64' },
  { id: 'nes', label: 'NES' },
  { id: 'genesis', label: 'Genesis' },
  { id: 'gb', label: 'Game Boy' },
  { id: 'snes', label: 'SNES' },
  { id: 'gba', label: 'GBA' },
  { id: 'n64', label: 'N64' },
];

const $ = (id) => document.getElementById(id);
const stripDiacritics = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const fmt = (n) => n.toLocaleString('en-US');
const sessions = new WeakMap();
const principal = () => authState.getCurrentUser()?.id || '';
function session(app) {
  if (!sessions.has(app)) sessions.set(app, { epoch: 0, romGen: 0, topGen: 0, principal: principal() });
  return sessions.get(app);
}
const allowed = (app) => app.state === 'solo'
  || (app.state === 'playing' && app.net?.playerNumber === 1 && app._netMode !== 'link-cable');
const scopes = (app) => ({ ach: app._gcWantAch === true, score: app._gcWantScore === true });
function searchFor(app) {
  const state = session(app);
  state.search ||= createGameChangeSearch({
    current: () => state.open && app._gcSource === 'all' && state.principal === principal() ? state.epoch : null,
    scopes: () => scopes(app),
    systemAvailable,
    makeRow: (g, beforePick) => makeRow(app, { system: g.system_id, romName: g.rom_filename,
      title: g.title, hash: g.hash, showSystem: true, metadata: g, playable: g.playable, beforePick }),
    resetScopes: () => {
      setToggle(app, '_gcWantAch', $('gc-filter-ach'), false);
      setToggle(app, '_gcWantScore', $('gc-filter-score'), false);
      saveScopes(scopes(app));
      state.search.update();
    },
  });
  return state.search;
}

function isLocalHost() {
  const h = location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.trycloudflare.com');
}

function systemAvailable(system) {
  const family = mergedSystems(system)[0];
  return SYSTEM_ORDER.some((s) => s.id === family) && (isLocalHost() || moduleRegistry.isEnabled(`system:${family}`));
}

function systemLabel(id) {
  return SYSTEM_ORDER.find((s) => s.id === id)?.label || systemTag(id);
}

/** The list element that shows `source`. */
function listFor(source) {
  if (source === 'all') return $('gc-search-list');
  if (source === 'queue') return $('gc-queue-list');
  if (source === 'favorites') return $('gc-favorites-list');
  return $('gc-rom-library');
}

/**
 * The row's spoken name, in reading order: title, then playing, system and
 * features. Set explicitly because the visual order leads with the icons.
 */
function labelRow(row) {
  const parts = [row.dataset.title];
  if (row.getAttribute('aria-current') === 'true') parts.push('playing');
  if (row.dataset.systemLabel) parts.push(row.dataset.systemLabel);
  if (row.dataset.ach === '1') parts.push('achievements');
  if (row.dataset.score === '1') parts.push('high score');
  if (row.disabled) parts.push('ROM required');
  row.setAttribute('aria-label', parts.join(', '));
}

function emptyNote(text) {
  const el = document.createElement('p');
  el.className = 'gc-empty';
  el.textContent = text;
  return el;
}

/**
 * One launchable row: [icons] name [Playing] [SYSTEM]. The whole row is the
 * button, so the icon column and the tags are part of the click target.
 */
function makeRow(app, { system, romName, title, hash, showSystem, metadata, playable, beforePick }) {
  // Catalog members retain their own label and artwork, but launch through
  // the merged family's runtime and ROM download route.
  const runtimeSystem = mergedSystems(system)[0];
  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'rom-entry gc-row';
  row.title = title;
  row.dataset.title = title;
  row.dataset.name = String(title).toLowerCase();
  row.dataset.gcSystem = runtimeSystem || '';
  if (hash) row.dataset.gcHash = hash;
  row.dataset.systemLabel = systemLabel(system);
  row.disabled = playable === false || !romName || !systemAvailable(system);
  const epoch = session(app).epoch;
  const owner = principal();
  const art = document.createElement('span');
  art.className = 'gc-art';
  art.setAttribute('aria-hidden', 'true');
  const label = buildCoverLabel(document, { title: Array.from(title || '')[0] || '', system: runtimeSystem });
  label.querySelector('.home-cover-label-band').textContent = '';
  art.append(label);
  if (metadata?.game_id != null && (metadata.cover_url || metadata.has_cover)) {
    const img = document.createElement('img');
    img.src = `/api/library/artwork/cover/${encodeURIComponent(metadata.game_id)}`;
    img.alt = '';
    img.width = 33;
    img.height = 44;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.addEventListener('error', () => img.remove(), { once: true });
    art.append(img);
  }
  row.append(art);

  const badges = document.createElement('span');
  badges.className = 'gc-badges';
  badges.setAttribute('aria-hidden', 'true');

  const name = document.createElement('span');
  name.className = 'rom-entry-name';
  name.textContent = title;
  row.appendChild(name);

  // The running game: by ROM hash, or by filename on the same system for C64
  // disk bundles, whose session hash differs from the catalog's.
  const playing = (hash && app.romHash && hash === app.romHash)
    || (romName && romName === app._activeRomFilename && runtimeSystem === app.system?.id);
  if (playing) {
    row.setAttribute('aria-current', 'true');
    const playing = document.createElement('span');
    playing.className = 'gc-playing';
    playing.textContent = 'Playing';
    row.appendChild(playing);
  }

  if (showSystem) {
    const tag = document.createElement('span');
    tag.className = 'gc-system-tag';
    tag.textContent = systemTag(system);
    row.appendChild(tag);
  }

  if (row.disabled) {
    row.classList.add('gc-locked');
    const note = document.createElement('span');
    note.className = 'gc-locked-note';
    note.textContent = 'ROM required';
    row.append(note);
  }
  row.append(badges);
  if (metadata && ('achievement_count' in metadata || 'score_supported' in metadata)) {
    fillBadges(row, { hasAch: metadata.achievement_count > 0, hasScore: metadata.score_supported === true,
      achCount: metadata.achievement_count || 0 });
  }

  labelRow(row);
  row.addEventListener('click', () => {
    if (row.disabled || !allowed(app) || !row.isConnected || row.closest('.hidden')
      || epoch !== session(app).epoch || owner !== principal()) return;
    beforePick?.();
    app._requestGameChange(runtimeSystem, romName);
  });
  return row;
}

/** A queue or favorites row (cross-system, so it carries the system tag). */
export function gcMakeTopEntry(app, system, romName, title, hash, metadata) {
  return makeRow(app, { system, romName, title, hash, showSystem: true, metadata });
}

/**
 * Achievement / high-score support for a ROM by hash, from the cheevos
 * system cache. Null when the game supports neither or the system's
 * definitions are not loaded. Mirrors `_stampRomBadges` on the home list.
 */
export function gcBadgeInfo(app, systemId, hash) {
  if (!hash) return null;
  const game = app.cheevosEngine?._systemCache?.get(systemId)?.byHash?.get(hash);
  if (!game) return null;
  const hasAch = Array.isArray(game.achievements) && game.achievements.length > 0;
  const hasScore = scoreSpecSupported(game.scoreSpec);
  if (!hasAch && !hasScore) return null;
  return { hasAch, hasScore, achCount: hasAch ? game.achievements.length : 0 };
}

function fillBadges(row, info) {
  row.dataset.ach = info?.hasAch ? '1' : '';
  row.dataset.score = info?.hasScore ? '1' : '';
  labelRow(row);
  const slot = row.querySelector('.gc-badges');
  if (!slot) return;
  slot.replaceChildren();
  if (!info) return;
  slot.classList.add('rom-badge');
  slot.title = [
    info.hasAch ? `${info.achCount} achievements` : '',
    info.hasScore ? 'High score tracking' : '',
  ].filter(Boolean).join(', ');
  if (info.hasAch) slot.appendChild(badgeIconEl('achievements'));
  if (info.hasScore) slot.appendChild(badgeIconEl('score'));
}

/**
 * Load each system's (large) definitions in the background and stamp the
 * icons onto the rows already in `list`. Never blocks rendering. A newer
 * render of the same list (a system switch) supersedes this pass.
 */
export function gcStampBadges(app, list, systems, { retry = false } = {}) {
  if (!list) return Promise.resolve();
  const state = session(app);
  const epoch = state.epoch;
  const owner = principal();
  const gen = String((Number(list.dataset.gcGen) || 0) + 1);
  list.dataset.gcGen = gen;
  const current = () => list.dataset.gcGen === gen && state.epoch === epoch && owner === principal();
  list.classList.remove('gc-badges-none');
  list.querySelector('.gc-metadata-error')?.remove();
  delete list.dataset.metadataError;
  if (!systems.length || !app.cheevosEngine?.loadSystem) {
    list.classList.add('gc-badges-none');
    return Promise.resolve();
  }
  list.dataset.badges = 'loading';
  const failures = new Set();
  const passes = systems.map((s) => Promise.resolve()
    .then(() => app.cheevosEngine.loadSystem(s, { retry }))
    .then((entry) => {
      if (!current()) return;
      const status = entry?.loadStatus || app.cheevosEngine._systemCache?.get(s)?.loadStatus;
      if (status && status !== 'complete') failures.add(s);
      list.querySelectorAll(`.gc-row[data-gc-system="${s}"]`).forEach((row) => {
        fillBadges(row, gcBadgeInfo(app, s, row.dataset.gcHash));
      });
    })
    .catch(() => { if (current()) failures.add(s); }));
  return Promise.all(passes).then(() => {
    if (!current()) return;
    delete list.dataset.badges;
    if (failures.size) {
      list.dataset.metadataError = '1';
      const note = document.createElement('div');
      note.className = 'gc-metadata-error gc-empty';
      note.textContent = "Couldn't check achievements and high scores for every game. ";
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'gc-retry';
      button.textContent = 'Try again';
      button.addEventListener('click', () => gcStampBadges(app, list, systems, { retry: true }));
      note.append(button);
      list.prepend(note);
    }
    list.classList.toggle('gc-badges-none', !failures.size && !list.querySelector('.gc-badges .rp-badge-icon'));
    if (app._gcSource !== 'all') applyFilter(app);
  });
}

/** Render the current system's full ROM list into the system list. */
export async function loadGameChangeRomList(app, systemId) {
  const list = $('gc-rom-library');
  if (!list) return;
  const state = session(app);
  const epoch = state.epoch;
  const gen = ++state.romGen;
  const owner = principal();
  state.romController?.abort();
  const controller = state.romController = new AbortController();
  const current = () => state.epoch === epoch && state.romGen === gen && owner === principal()
    && app._gcSelectedSystem === systemId;
  list.dataset.gcGen = String((Number(list.dataset.gcGen) || 0) + 1);
  list.innerHTML = '';
  delete list.dataset.metadataError;
  delete list.dataset.badges;
  list.scrollTop = 0;
  delete list.dataset.loaded;
  app._gcSelectedSystem = systemId;
  setCount(app, 'Loading…');

  let roms;
  try {
    const res = await fetch(`/api/roms?system=${encodeURIComponent(systemId)}`, { signal: controller.signal });
    if (!current()) return;
    if (!res.ok) throw new Error('roms');
    roms = await res.json();
  } catch {
    roms = null;
  }
  // Re-entrant on system switch: a newer load owns the list.
  if (!current()) return;
  if (!Array.isArray(roms)) {
    const note = emptyNote(`Couldn't load the ${systemLabel(systemId)} games.`);
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'gc-retry';
    retry.textContent = 'Try again';
    retry.addEventListener('click', () => loadGameChangeRomList(app, systemId));
    list.append(note, retry);
    applyFilter(app);
    return;
  }
  list.dataset.loaded = '1';
  // C64 disk folders load as bundles through /disks (_fetchAndSetRom, PR #651).
  app._multiDiskRoms ||= {};
  app._multiDiskRoms[systemId] = new Set(roms.filter((rom) => rom.type === 'directory').map((rom) => rom.name));
  if (!roms.length) {
    list.appendChild(emptyNote(`No ${systemLabel(systemId)} games yet.`));
    applyFilter(app);
    return;
  }

  const displayNames = app._resolveDisplayNames(roms);
  const frag = document.createDocumentFragment();
  for (const rom of roms) {
    frag.appendChild(makeRow(app, {
      system: systemId,
      romName: rom.name,
      title: displayNames.get(rom.name) || rom.title || rom.name,
      hash: rom.hash,
      showSystem: false,
      metadata: rom,
    }));
  }
  list.appendChild(frag);
  applyFilter(app);
  gcStampBadges(app, list, [systemId]);
}

/**
 * Render the queue (signed in) and favorites lists. Rows appear without
 * waiting on any definitions; icons follow from the background pass.
 */
export async function loadGameChangeTop(app) {
  const queueList = $('gc-queue-list');
  const favList = $('gc-favorites-list');
  if (!queueList || !favList) return;
  const state = session(app);
  const epoch = state.epoch;
  const gen = ++state.topGen;
  const owner = principal();
  state.topController?.abort();
  const controller = state.topController = new AbortController();
  const current = () => epoch === state.epoch && gen === state.topGen && owner === principal();
  for (const list of [queueList, favList]) {
    list.dataset.gcGen = String((Number(list.dataset.gcGen) || 0) + 1);
    delete list.dataset.metadataError;
    delete list.dataset.badges;
  }
  queueList.innerHTML = '';
  favList.innerHTML = '';
  const signedIn = !!authState.getCurrentUser();

  // Favorites come from the synced localStorage cache, so anonymous players
  // have them too.
  const favs = app._getFavorites().filter((f) => f.name && systemAvailable(f.system));
  for (const fav of favs) {
    const title = app._romDisplayTitle(fav.name, fav.system);
    favList.appendChild(gcMakeTopEntry(app, fav.system, fav.name, title, fav.hash));
  }
  if (!favs.length) favList.appendChild(emptyNote('No favorites yet. Star games in the Library and they show up here.'));
  setSourceCount('favorites', favs.length);

  // The queue is a signed-in account feature; fetch it fresh.
  let queueItems = [];
  let queueFailed = false;
  if (signedIn) {
    queueList.dataset.badges = 'loading';
    try {
      const res = await fetch('/api/library/queue', { credentials: 'include', signal: controller.signal });
      if (!current()) return;
      if (!res.ok) throw new Error(String(res.status));
      const queue = await res.json();
      if (!current()) return;
      queueItems = (queue.items || []).filter((it) => it.rom_filename
        && systemAvailable(it.rom_system || it.game?.system_id));
    } catch {
      queueFailed = true;
    }
    if (!current()) return;
    for (const item of queueItems) {
      const system = item.rom_system || item.game?.system_id;
      queueList.appendChild(gcMakeTopEntry(app, system, item.rom_filename,
        item.game?.title || item.rom_filename, item.rom_hash, { ...item.game, game_id: item.game?.game_id ?? item.game?.id }));
    }
    if (!queueItems.length) {
      queueList.appendChild(emptyNote(queueFailed
        ? "Couldn't load your queue."
        : 'Your queue is empty. Add games from the Library to line up what you play next.'));
    }
    if (queueFailed) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'gc-retry';
      retry.textContent = 'Try again';
      retry.addEventListener('click', () => loadGameChangeTop(app));
      queueList.append(retry);
    }
    setSourceCount('queue', queueFailed ? null : queueItems.length);
  }
  app._gcTopCounts = { queue: queueItems.length, favorites: favs.length };
  if (app._gcSource === 'queue' || app._gcSource === 'favorites') applyFilter(app);

  const systemsOf = (rows) => [...new Set(rows)].filter(Boolean);
  if (app._gcSource === 'favorites') stampTop(app, 'favorites');
  else if (!app._gcSource) gcStampBadges(app, favList, systemsOf(favs.map((f) => f.system)));
  if (signedIn && (!app._gcSource || app._gcSource === 'queue')) gcStampBadges(app, queueList, systemsOf(queueItems.map((it) => it.rom_system || it.game?.system_id)));
}

function setSourceCount(source, n) {
  const el = document.querySelector(`#gc-sources [data-gc-source="${source}"] .gc-source-count`);
  if (el) el.textContent = n == null ? '' : String(n);
}

function stampTop(app, source) {
  const list = listFor(source);
  if (!list) return;
  const rows = [...list.querySelectorAll('.gc-row')];
  const systems = [...new Set(rows.map((r) => r.dataset.gcSystem))];
  gcStampBadges(app, list, systems);
  if (source !== 'favorites') return;
  const state = session(app);
  const epoch = state.epoch;
  const owner = principal();
  const gen = list.dataset.gcGen;
  for (const system of systems) {
    Promise.resolve().then(async () => {
      const response = await fetch(`/api/roms?system=${encodeURIComponent(system)}`);
      if (!response.ok) return;
      const roms = await response.json();
      if (!Array.isArray(roms) || state.epoch !== epoch || principal() !== owner || list.dataset.gcGen !== gen
        || app._gcSource !== 'favorites') return;
      for (const row of rows.filter((r) => r.dataset.gcSystem === system)) {
        const favorite = app._getFavorites().find((f) => f.system === system && f.hash === row.dataset.gcHash);
        const rom = roms.find((r) => r.hash === row.dataset.gcHash && r.name === favorite?.name)
          || roms.find((r) => r.hash === row.dataset.gcHash);
        if (!rom?.has_cover || rom.game_id == null || row.querySelector('img')) continue;
        const img = document.createElement('img');
        img.src = `/api/library/artwork/cover/${encodeURIComponent(rom.game_id)}`;
        img.alt = '';
        img.width = 33;
        img.height = 44;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.addEventListener('error', () => img.remove(), { once: true });
        row.querySelector('.gc-art').append(img);
      }
    }).catch(() => {});
  }
}

function setCount(app, text) {
  const el = $('gc-count');
  if (el && el.textContent !== text) el.textContent = text;
}

/** Build the source rail: queue (signed in), favorites, then each system. */
function buildSources(app) {
  const rail = $('gc-sources');
  if (!rail) return [];
  rail.innerHTML = '';
  const sources = [{ id: 'all', label: 'All games' }];
  if (authState.getCurrentUser()) sources.push({ id: 'queue', label: 'My queue', count: true, sepBefore: true });
  sources.push({ id: 'favorites', label: 'Favorites', count: true, sepBefore: !authState.getCurrentUser() });
  const systems = SYSTEM_ORDER.filter(({ id }) => systemAvailable(id));
  systems.forEach((s, i) => sources.push({ ...s, sepBefore: i === 0 }));

  for (const src of sources) {
    if (src.sepBefore) {
      const sep = document.createElement('span');
      sep.className = 'gc-sources-sep';
      sep.setAttribute('aria-hidden', 'true');
      rail.appendChild(sep);
    }
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'gc-source';
    tab.id = `gc-src-${src.id}`;
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-selected', 'false');
    tab.setAttribute('aria-controls', 'gc-panel');
    tab.tabIndex = -1;
    tab.dataset.gcSource = src.id;
    const label = document.createElement('span');
    label.className = 'gc-source-label';
    label.textContent = src.label;
    tab.appendChild(label);
    if (src.count) {
      const count = document.createElement('span');
      count.className = 'gc-source-count';
      tab.appendChild(count);
    }
    rail.appendChild(tab);
  }
  return sources.map((s) => s.id);
}

const PLACEHOLDER = {
  all: 'Search all games',
  queue: 'Search your queue',
  favorites: 'Search favorites',
};

/** Show `source`'s list and select its tab (selection follows focus). */
export function gcSetSource(app, source, { focus = false } = {}) {
  const tab = document.querySelector(`#gc-sources [data-gc-source="${source}"]`);
  if (!tab) return;
  const state = session(app);
  searchFor(app).cancel();
  state.romGen += 1;
  state.romController?.abort();
  for (const id of ['gc-rom-library', 'gc-queue-list', 'gc-favorites-list']) {
    const list = $(id);
    if (list) list.dataset.gcGen = String((Number(list.dataset.gcGen) || 0) + 1);
  }
  app._gcSource = source;
  document.querySelectorAll('#gc-sources .gc-source').forEach((t) => {
    const on = t === tab;
    t.setAttribute('aria-selected', on ? 'true' : 'false');
    t.tabIndex = on ? 0 : -1;
  });
  $('gc-panel')?.setAttribute('aria-labelledby', tab.id);
  for (const s of ['all', 'queue', 'favorites', 'system']) {
    const list = listFor(s);
    const shown = s === source || (s === 'system' && !['all', 'queue', 'favorites'].includes(source));
    list?.classList.toggle('hidden', !shown);
  }
  const isSystem = !['all', 'queue', 'favorites'].includes(source);
  $('gc-no-match')?.classList.add('hidden');
  const filter = $('gc-rom-filter');
  if (filter) {
    const text = PLACEHOLDER[source] || `Search ${systemLabel(source)} games`;
    filter.placeholder = text;
    filter.setAttribute('aria-label', text);
  }
  if (focus) tab.focus();
  tab.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  if (source === 'all') searchFor(app).update();
  else if (isSystem && (app._gcSelectedSystem !== source || !$('gc-rom-library')?.dataset.loaded)) {
    loadGameChangeRomList(app, source);
  } else {
    applyFilter(app);
    if (isSystem) gcStampBadges(app, $('gc-rom-library'), [source]);
    else stampTop(app, source);
  }
}

/**
 * Apply the search text and the two toggles to the showing list, then update
 * the result count and the no-match note.
 */
export function applyFilter(app) {
  const source = app._gcSource;
  if (source === 'all') { searchFor(app).update(); return; }
  const list = listFor(source);
  const noMatch = $('gc-no-match');
  if (!list || list.classList.contains('hidden')) return;
  const raw = ($('gc-rom-filter')?.value || '').trim();
  const q = stripDiacritics(raw.toLowerCase());
  const wantAch = app._gcWantAch === true;
  const wantScore = app._gcWantScore === true;
  const rows = list.querySelectorAll('.gc-row');
  let shown = 0;
  for (const row of rows) {
    const hide = (q && !stripDiacritics(row.dataset.name || '').includes(q))
      || (wantAch && row.dataset.ach !== '1')
      || (wantScore && row.dataset.score !== '1');
    row.classList.toggle('hidden', hide);
    if (!hide) shown += 1;
  }
  const total = rows.length;
  const loadingSystem = source !== 'queue' && source !== 'favorites' && !list.dataset.loaded
    && !list.querySelector('.gc-empty');
  if (loadingSystem) setCount(app, 'Loading…');
  else if (!total) setCount(app, '');
  else if (shown === total) setCount(app, total === 1 ? '1 game' : `${fmt(total)} games`);
  else setCount(app, `${fmt(shown)} of ${fmt(total)}`);

  if (!noMatch) return;
  const filtering = q || wantAch || wantScore;
  if (!total || shown || !filtering) {
    noMatch.classList.add('hidden');
    return;
  }
  const features = [wantAch ? 'achievements' : '', wantScore ? 'high scores' : ''].filter(Boolean).join(' and ');
  if (list.dataset.badges === 'loading' && features) noMatch.textContent = `Checking which games track ${features}…`;
  else if (list.dataset.metadataError && features) noMatch.textContent = "Couldn't check achievements and high scores for every game. Try again.";
  else if (!q) noMatch.textContent = `No games here track ${features}.`;
  else if (features) noMatch.textContent = `No games with ${features} match "${raw}".`;
  else noMatch.textContent = `No games match "${raw}".`;
  noMatch.classList.remove('hidden');
}

function setToggle(app, key, btn, on) {
  app[key] = on;
  btn?.setAttribute('aria-pressed', on ? 'true' : 'false');
}

export function showGameChangeOverlay(app) {
  if (!allowed(app)) return;
  const overlay = $('game-change-overlay');
  if (!overlay) return;

  const state = session(app);
  if (state.open) return;
  app.acquireGameplayInput('game-change');
  try {
    state.epoch += 1;
    state.open = true;
    state.principal = principal();
    buildSources(app);
    $('gc-note')?.classList.toggle('hidden', app.state !== 'playing');

    const filter = $('gc-rom-filter');
    if (filter) filter.value = '';
    const stored = loadScopes();
    setToggle(app, '_gcWantAch', $('gc-filter-ach'), stored.ach);
    setToggle(app, '_gcWantScore', $('gc-filter-score'), stored.score);
    // The system list is rebuilt on open so the Playing mark follows the game.
    delete $('gc-rom-library')?.dataset.loaded;
    app._gcSelectedSystem = null;

    gcSetSource(app, 'all');
    loadGameChangeTop(app);

    noteSurfaceOpened(overlay);
    overlay.classList.remove('hidden');
    const viewport = window.visualViewport;
    if (viewport) {
      const resize = () => {
        if (viewport.width <= 680 || viewport.height <= 600) {
          overlay.style.top = `${viewport.offsetTop}px`;
          overlay.style.height = `${viewport.height}px`;
          overlay.style.bottom = 'auto';
        } else {
          overlay.style.removeProperty('top');
          overlay.style.removeProperty('height');
          overlay.style.removeProperty('bottom');
        }
      };
      viewport.addEventListener('resize', resize);
      viewport.addEventListener('scroll', resize);
      state.viewportCleanup = () => {
        viewport.removeEventListener('resize', resize);
        viewport.removeEventListener('scroll', resize);
        for (const property of ['top', 'height', 'bottom']) overlay.style.removeProperty(property);
      };
      resize();
    }
    filter?.focus();
  } catch (error) {
    hideGameChangeOverlay(app);
    throw error;
  }
}

export function hideGameChangeOverlay(app) {
  const state = session(app);
  state.open = false;
  state.epoch += 1;
  state.romGen += 1;
  state.topGen += 1;
  state.search?.cancel();
  state.romController?.abort();
  state.topController?.abort();
  state.viewportCleanup?.();
  state.viewportCleanup = null;
  app._pendingGameChange = null;
  $('game-change-overlay')?.classList.add('hidden');
  app.releaseGameplayInput('game-change');
  app.canvas?.focus();
}

function visibleRows() {
  const list = listFor(document.querySelector('#gc-sources [aria-selected="true"]')?.dataset.gcSource);
  return list ? [...list.querySelectorAll('.gc-row:not(.hidden):not(:disabled)')] : [];
}

/** Wire the overlay's controls once, from `_bindUI`. */
export function bindGameChangeOverlay(app) {
  const overlay = $('game-change-overlay');
  if (!overlay) return;
  const state = session(app);
  if (state.bound === overlay) return;
  state.bound = overlay;
  let user = principal();
  authState.subscribe?.(() => {
    const next = principal();
    if (next === user) return;
    user = next;
    hideGameChangeOverlay(app);
    $('gc-queue-list')?.replaceChildren();
    $('gc-favorites-list')?.replaceChildren();
    app._gcTopCounts = null;
  });
  const close = () => {
    app._hideGameChangeOverlay();
    app._pendingGameChange = null;
  };
  $('gc-close')?.addEventListener('click', close);

  // A press that starts and ends on the backdrop closes; a drag that only
  // ends there (text selection in the search box) does not.
  let downOnBackdrop = false;
  overlay.addEventListener('pointerdown', (e) => { downOnBackdrop = e.target === overlay; });
  overlay.addEventListener('pointercancel', () => { downOnBackdrop = false; });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay && downOnBackdrop) close();
    downOnBackdrop = false;
  });

  $('gc-rom-filter')?.addEventListener('input', () => applyFilter(app));
  $('gc-rom-filter')?.addEventListener('keydown', (e) => {
    if (e.isComposing || e.keyCode === 229 || e.key !== 'ArrowDown') return;
    const first = visibleRows()[0];
    if (!first) return;
    e.preventDefault();
    first.focus();
  });

  for (const [id, key] of [['gc-filter-ach', '_gcWantAch'], ['gc-filter-score', '_gcWantScore']]) {
    const btn = $(id);
    btn?.addEventListener('click', () => {
      setToggle(app, key, btn, app[key] !== true);
      saveScopes(scopes(app));
      applyFilter(app);
    });
  }

  const rail = $('gc-sources');
  rail?.addEventListener('click', (e) => {
    const tab = e.target.closest('.gc-source');
    if (!tab) return;
    app._gcPickedSource = true;
    gcSetSource(app, tab.dataset.gcSource);
  });
  // Arrow keys (either axis, since the rail turns horizontal on phones),
  // Home and End move between sources; selection follows focus.
  rail?.addEventListener('keydown', (e) => {
    if (e.isComposing || e.keyCode === 229) return;
    const tabs = [...rail.querySelectorAll('.gc-source')];
    const index = tabs.indexOf(document.activeElement);
    if (index < 0) return;
    let next = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    else if (e.key === 'Home') next = tabs[0];
    else if (e.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    e.preventDefault();
    app._gcPickedSource = true;
    gcSetSource(app, next.dataset.gcSource, { focus: true });
  });

  // Up and Down walk the visible rows; Up from the first returns to search.
  $('gc-panel')?.addEventListener('keydown', (e) => {
    if (e.isComposing || e.keyCode === 229) return;
    const row = e.target.closest?.('.gc-row');
    if (!row || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const rows = visibleRows();
    const i = rows.indexOf(row);
    let next = null;
    if (e.key === 'ArrowDown') next = rows[i + 1];
    else if (e.key === 'ArrowUp') next = i > 0 ? rows[i - 1] : $('gc-rom-filter');
    else if (e.key === 'Home') next = rows[0];
    else if (e.key === 'End') next = rows[rows.length - 1];
    e.preventDefault();
    next?.focus();
  });
}
